export type ProjectHeldFundsBreakdownItem = {
  contractId: string | null;
  contractNumber: number | null;
  milestoneId: string | null;
  milestoneNumber: number | null;
  milestoneDescription: string | null;
  amountOre: number;
  amountSek: number;
  currency: string;
  status: string;
};

export type ProjectHeldFundsSummary = {
  amountOre: number;
  amountSek: number;
  currency: string;
  items: ProjectHeldFundsBreakdownItem[];
};

const terminalEscrowStatuses = new Set([
  'released',
  'refunded',
  'fees_collected',
]);

const finiteNumber = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const positiveNumberOrNull = (value: unknown) => {
  const number = finiteNumber(value);
  return number > 0 ? number : null;
};

const stringOrNull = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const transactionHeldAmountOre = (transaction: any) => {
  if (terminalEscrowStatuses.has(transaction?.status)) return 0;
  if (typeof transaction?.amountOre === 'number') {
    return Math.max(transaction.amountOre, 0);
  }
  return Math.max(finiteNumber(transaction?.amountSek) * 100, 0);
};

export const isActiveProjectHeldFundsTransaction = (transaction: any) =>
  transaction?.type === 'escrow_held' &&
  transactionHeldAmountOre(transaction) > 0;

export const deriveProjectHeldFundsSummary = (
  transactions: any[],
): ProjectHeldFundsSummary => {
  const grouped = new Map<string, ProjectHeldFundsBreakdownItem>();
  for (const transaction of transactions) {
    if (!isActiveProjectHeldFundsTransaction(transaction)) continue;
    const metadata = transaction.metadata ?? {};
    const contractId = stringOrNull(
      metadata.contractId ?? transaction.contractId,
    );
    const milestoneId = stringOrNull(
      metadata.milestoneId ?? transaction.milestoneId,
    );
    const contractNumber = positiveNumberOrNull(
      metadata.contractNumber ?? transaction.contractNumber,
    );
    const milestoneNumber = positiveNumberOrNull(
      metadata.milestoneNumber ?? transaction.milestoneNumber,
    );
    const paymentId =
      stringOrNull(transaction.paymentId) ?? stringOrNull(transaction.id);
    const key = [
      contractId ?? `contract-${contractNumber ?? 'unknown'}`,
      milestoneId ?? `milestone-${milestoneNumber ?? paymentId ?? 'unknown'}`,
    ].join(':');
    const amountOre = transactionHeldAmountOre(transaction);
    const existing = grouped.get(key);
    if (existing) {
      existing.amountOre += amountOre;
      existing.amountSek = Math.round(existing.amountOre / 100);
      if (existing.status !== transaction.status) existing.status = 'held';
      continue;
    }
    grouped.set(key, {
      contractId,
      contractNumber,
      milestoneId,
      milestoneNumber,
      milestoneDescription: stringOrNull(
        metadata.milestoneDescription ?? transaction.milestoneDescription,
      ),
      amountOre,
      amountSek: Math.round(amountOre / 100),
      currency: stringOrNull(transaction.currency) ?? 'SEK',
      status: stringOrNull(transaction.status) ?? 'held',
    });
  }
  const items = [...grouped.values()].sort(
    (left, right) =>
      (left.contractNumber ?? Number.MAX_SAFE_INTEGER) -
        (right.contractNumber ?? Number.MAX_SAFE_INTEGER) ||
      (left.milestoneNumber ?? Number.MAX_SAFE_INTEGER) -
        (right.milestoneNumber ?? Number.MAX_SAFE_INTEGER) ||
      (left.contractId ?? '').localeCompare(right.contractId ?? '') ||
      (left.milestoneId ?? '').localeCompare(right.milestoneId ?? ''),
  );
  const amountOre = items.reduce((total, item) => total + item.amountOre, 0);
  return {
    amountOre,
    amountSek: Math.round(amountOre / 100),
    currency: items[0]?.currency ?? 'SEK',
    items,
  };
};

export const projectHeldFundsSummaryFromTimeline = (
  timeline: any,
  transactions: any[],
): ProjectHeldFundsSummary => {
  const summary = timeline?.heldFundsSummary;
  if (
    summary &&
    typeof summary === 'object' &&
    Array.isArray(summary.items) &&
    typeof summary.amountOre === 'number'
  ) {
    return summary as ProjectHeldFundsSummary;
  }
  return deriveProjectHeldFundsSummary(transactions);
};

export type ProjectFundsTotals = {
  paidOutOre: number;
  refundedOre: number;
  /** Null when the viewer's timeline has no deposits (the company never sees them). */
  depositedOre: number | null;
};

const itemAmountOre = (transaction: any) =>
  typeof transaction?.amountOre === 'number'
    ? Math.max(transaction.amountOre, 0)
    : Math.max(finiteNumber(transaction?.amountSek) * 100, 0);

/**
 * Totals next to the held funds (ClickUp 869fmxjbb), summed from the rows
 * the viewer already sees in the payment timeline, in the viewer's amounts.
 */
export const deriveProjectFundsTotals = (
  transactions: any[],
): ProjectFundsTotals => {
  let paidOutOre = 0;
  let refundedOre = 0;
  let depositedOre = 0;
  let hasDeposits = false;
  for (const transaction of transactions) {
    const amountOre = itemAmountOre(transaction);
    switch (transaction?.type) {
      case 'ping_payout_confirmed':
      case 'payout_booked':
        paidOutOre += amountOre;
        break;
      case 'payment_refund':
        refundedOre += amountOre;
        break;
      case 'payment_refund_reversed':
        refundedOre -= amountOre;
        break;
      case 'payment_received':
        hasDeposits = true;
        depositedOre += amountOre;
        break;
      default:
        break;
    }
  }
  return {
    paidOutOre,
    refundedOre: Math.max(refundedOre, 0),
    depositedOre: hasDeposits ? depositedOre : null,
  };
};
